import { createFileRoute, Link } from "@tanstack/react-router";
import { Navbar } from "@/components/Navbar";
import { HelpCircle, TrendingDown, TrendingUp, BarChart3, Target, Calculator, Sigma, Trophy, ArrowRight, History, Scale, Rocket, Heart, MessageSquare } from "lucide-react";

const SITE_URL = "https://vetokone.com";

const FAQ_ITEMS = [
  {
    q: "Mitä Vakion peliprosentit tarkoittavat?",
    a: "Veikkauksen Vakion peliprosentit kertovat, miten suomalaiset pelaajat jakavat panoksensa kohteen 1-, X- ja 2-merkkien välille. Ne eivät ole todennäköisyyksiä vaan pelaajien valintoja — siksi niiden vertaaminen markkinakertoimiin paljastaa ali- ja ylipelatut kohteet.",
  },
  {
    q: "Mikä on alipelattu kohde Vakiossa?",
    a: "Alipelattu kohde on sellainen, jonka peliprosentti on selvästi pienempi kuin kansainvälisten vedonvälittäjien arvioima todennäköisyys. Tällainen kohde voi tarjota paremman jakopotin, jos se toteutuu.",
  },
  {
    q: "Mikä on ylipelattu kohde?",
    a: "Ylipelattu kohde on sellainen, jonka peliprosentti on selvästi suurempi kuin markkinoiden todennäköisyys. Pelaajat suosivat näitä yli niiden todellisen arvon — tällaisten kohteiden kohdalla kannattaa harkita varmistamista.",
  },
  {
    q: "Miten käytän vakiovinkkejä rivin rakentamiseen?",
    a: "Etsi alipelattuja kohteita ja yhdistele niistä rivi. Vetokoneen rivisuositus (Järkevä) valitsee keskihintaisista riveistä sen, jossa hinnan ja odotusarvon välinen tasapaino on paras. Voit valita pelityylin (Minimi, Järkevä, Kattava, Maksimi) tarpeesi mukaan.",
  },
  {
    q: "Mistä markkinakertoimet tulevat?",
    a: "Markkinakertoimet kerätään reaaliajassa kansainvälisiltä vedonvälittäjiltä ESPN:n (DraftKings) sekä The Odds API- ja Odds-API.io-palveluiden kautta ja muunnetaan prosenteiksi (poistamalla bookmaker-marginaali) vertailua varten.",
  },
  {
    q: "Mitä Tulosveto-vertailu tekee?",
    a: "Tulosveto-vertailu laskee Poisson-mallilla todennäköisyyden jokaiselle lopputulokselle (esim. 2–1, 0–0) käyttäen pohjana kansainvälisiä 1X2-markkinakertoimia. Mallin antamia todennäköisyyksiä verrataan Veikkauksen tulosvedon kertoimiin, jolloin näet missä lopputuloksissa Veikkauksen kerroin on korkeampi kuin mitä matemaattinen malli antaisi — eli missä on potentiaalista arvoa.",
  },
  {
    q: "Mikä Poisson-malli on?",
    a: "Poisson-malli arvioi kunkin joukkueen odotetun maalimäärän markkinakertoimista ja laskee sen pohjalta todennäköisyyden jokaiselle 0–0…5–5 lopputulokselle. Se ei tunne joukkueiden vireitä tai loukkaantumisia — se on puhdas tilastollinen vertailupohja. Jos yli/alle 2,5 -kertoimet ovat saatavilla, niitä käytetään 1X2-kertoimien rinnalla maalimäärän arvioinnissa, ja pienten tulosten (0–0, 1–0, 0–1, 1–1) todennäköisyyksiä tarkennetaan Dixon–Coles-korjauksella.",
  },
  {
    q: "Mitä Moniveto-vertailu tekee?",
    a: "Moniveto (Veikkauksen MULTISCORE) on peli, jossa arvataan 4–8 ottelun tarkat lopputulokset. Vetokone laskee jokaiselle ottelulle Poisson-mallilla todennäköisyysjakauman kaikille 0–0…5–5 tuloksille ja vertaa niitä Veikkauksen kertoimiin. Näet suoraan mitkä yksittäiset lopputulokset ja koko rivit ovat arvokkaimpia.",
  },
  {
    q: "Miten Monivedon rivisuositukset toimivat?",
    a: "Vetokone järjestää jokaisen ottelun lopputulokset arvon mukaan (Veikkauksen kerroin vs. Poisson-mallin reilu kerroin) ja rakentaa siitä TOP-rivit. Voit selata suositellut yhdistelmät ja nähdä kummankin rivin osumistodennäköisyyden ja odotusarvon.",
  },
  {
    q: "Miten löydän ylikertoimia Pitkävedosta?",
    a: "Pitkävedossa ylikerroin löytyy vertaamalla Veikkauksen kerrointa markkinakertoimiin: kun Veikkauksen tarjoama kerroin on korkeampi kuin todennäköisyyden käänteisluku, kohde on ylikerroin. Lue tarkemmin oppaastamme Pitkävedon ylikertoimista.",
  },
  {
    q: "Onko Vetokoneen käyttö ilmaista?",
    a: "Kyllä. Vetokone on täysin ilmainen Vakio-, Tulosveto- ja Moniveto-analyysityökalu. Emme ota välityspalkkioita emmekä tarjoa vedonlyöntiä — autamme vain analysoimaan Veikkauksen kohteita.",
  },
];

export const Route = createFileRoute("/ohjeet")({
  component: OhjeetPage,
  head: () => ({
    meta: [
      { title: "Ohjeet ja tietoa – Näin käytät Vetokonetta" },
      { name: "description", content: "Opi miten Vakion peliprosentit, Tulosvedon Poisson-vertailu ja Monivedon tulosjakaumat toimivat. Lue myös Vetokoneen tausta ja periaatteet." },
      { name: "keywords", content: "vakio ohjeet, vakiovinkit, tulosveto ohjeet, moniveto ohjeet, multiscore, ylipelattu kohde, alipelattu kohde, vakiorivi, poisson malli, vetokone tietoa" },
      { property: "og:title", content: "Ohjeet ja tietoa – Vetokone" },
      { property: "og:description", content: "Opi käyttämään Vakion, Tulosvedon ja Monivedon vertailutyökaluja ja lue Vetokoneen tausta." },
      { property: "og:url", content: `${SITE_URL}/ohjeet` },
      { property: "og:image", content: `${SITE_URL}/og-image.jpg` },
      { name: "twitter:title", content: "Ohjeet ja tietoa – Vetokone" },
      { name: "twitter:description", content: "Opi käyttämään Vakion, Tulosvedon ja Monivedon vertailutyökaluja ja lue Vetokoneen tausta." },
      { name: "twitter:image", content: `${SITE_URL}/og-image.jpg` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/ohjeet` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ_ITEMS.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.a,
            },
          })),
        }),
      },
    ],
  }),
});

function OhjeetPage() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="mx-auto max-w-3xl px-4 py-8 space-y-8">
        <div className="animate-slide-up">
          <h1 className="text-2xl font-bold text-foreground mb-2">Näin käytät Vakiovertailua</h1>
          <p className="text-muted-foreground">
            Vakiovertailu auttaa löytämään vakiokohteita, joiden peliprosentit eroavat merkittävästi
            kansainvälisten vedonlyöntimarkkinoiden todennäköisyyksistä.
          </p>
        </div>

        <h2 className="text-2xl font-bold text-foreground">Vakiovertailun perusteet</h2>

        <div className="grid gap-6">
          <InfoCard
            icon={BarChart3}
            title="Mitä peliprosentit kertovat?"
            content="Veikkauksen peliprosentit kertovat, miten pelaajat jakavat rahansa eri vaihtoehtojen (1, X, 2) välille. Ne eivät ole todennäköisyyksiä — ne heijastavat suomalaisten pelaajien mielipiteitä."
          />

          <InfoCard
            icon={Target}
            title="Mitä markkinakertoimet ovat?"
            content="Markkinakertoimet muunnetaan kansainvälisten vedonvälittäjien kertoimista prosenteiksi. Ne ovat yleensä tarkempi arvio todellisista todennäköisyyksistä, koska ne perustuvat laajempaan markkinaan."
          />

          <InfoCard
            icon={TrendingDown}
            title="Alipelattu kohde (vihreä)"
            color="underplayed"
            content="Vakion peliprosentti on pienempi kuin markkinoiden todennäköisyys. Vetokone käyttää kolmea rajaa: ero ≥ 5 prosenttiyksikköä = lievä alipeli (vaalea), ≥ 10 prosenttiyksikköä = selvä alipeli (kirkas), ≥ 20 prosenttiyksikköä = vahva alipeli (korostettu + lihavoitu). Mitä isompi ero, sitä enemmän kohde on jäänyt suomalaispelaajilta huomaamatta."
          />

          <InfoCard
            icon={TrendingUp}
            title="Ylipelattu kohde (punainen)"
            color="overplayed"
            content="Peliprosentti on suurempi kuin markkinoiden todennäköisyys. Samat rajat: ≥ 5 prosenttiyksikköä lievä, ≥ 10 selvä, ≥ 20 vahva ylipeli. Vahvasti ylipelatun suosikin kohdalla kannattaa harkita varmistamista toisella merkillä."
          />
        </div>

        <div className="glass rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <HelpCircle className="h-4 w-4 text-primary" />
            Näin pelaat näillä rajoilla
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex gap-2">
              <span className="text-primary font-bold">1.</span>
              <span><strong className="text-foreground">Päämerkkiä kannattaa harkita</strong> kohteista, joissa on selvä alipeli (ero ≥ 10 prosenttiyksikköä). Ne voivat olla kierroksen kiinnostavimpia arvokohteita.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary font-bold">2.</span>
              <span><strong className="text-foreground">Vahva ylipeli (≥ 20 prosenttiyksikköä)</strong> tarkoittaa että suomalaiset pelaavat suosikkia massiivisesti yli markkinan. Yhden merkin sijaan kannattaa harkita varmistamista 2 tai 3 merkillä — jos suosikki kompastuu, jakopotti voi olla hyvä.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary font-bold">3.</span>
              <span><strong className="text-foreground">Lievät erot (5–10 prosenttiyksikköä)</strong> ovat kohinaa: ne eivät yksin riitä perusteeksi. Käytä niitä lähinnä järjestämään kohteet kovuusjärjestykseen.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary font-bold">4.</span>
              <span><strong className="text-foreground">Hajautukset kohdistetaan kovimpiin</strong> otteluihin (suurin ero markkinaan tai tasaiset prosentit). Vetokone valitsee nämä automaattisesti — ks. alla.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary font-bold">5.</span>
              <span>Seuraa vaihdon kehitystä sulkeutumiseen asti. Prosentit muuttuvat ja arvot voivat ilmaantua viime hetkellä.</span>
            </li>
          </ul>
        </div>

        <div className="glass rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Calculator className="h-4 w-4 text-primary" />
            Mitä rivisuositukset (Minimi · Järkevä · Kattava · Maksimi) tarkoittavat?
          </h3>
          <p className="text-sm text-muted-foreground">
            Vetokone laskee jokaiselle ottelulle <em>arvojärjestyksen</em>: paras merkki ensin,
            sitten varmistukset suurimman alipelin / pienimmän ylipelin mukaan. Rivit rakennetaan
            näistä asteittain, ja näytöllä on neljä valmista ehdotusta:
          </p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li><strong className="text-foreground">Minimi</strong> — Halvin suositeltu rivi (vähintään 2 €): pääosin markkinasuosikit, ylipelatut suosikit suojattuina. Pieni osumistodennäköisyys.</li>
            <li><strong className="text-foreground">Järkevä ★</strong> — Hinnan ja odotusarvon välinen tasapainopiste. Koska Vakion voitot jaetaan poolista, rivi painottaa alipelattuja merkkejä eikä oletuksena varmista selvästi ylipelattua suosikkia (ero ≥ 10 prosenttiyksikköä) yhdellä merkillä.</li>
            <li><strong className="text-foreground">Kattava</strong> — Väli järkevän ja maksimin välillä; lisää hajautuksia kovimpiin otteluihin.</li>
            <li><strong className="text-foreground">Maksimi</strong> — Algoritmin kalleimmat hajautukset (katto 200 €).</li>
          </ul>
          <p className="text-xs text-muted-foreground">
            Voit myös lukita yksittäisiä merkkejä rivi-ikkunassa, jolloin algoritmi rakentaa muut hajautukset niiden ympärille.
          </p>
        </div>

        <div className="pt-4 border-t border-border/40">
          <h2 className="text-2xl font-bold text-foreground mb-2">Näin käytät Tulosvetoa</h2>
          <p className="text-muted-foreground">
            Tulosveto-vertailu auttaa löytämään lopputuloksia, joiden Veikkauksen kerroin on
            korkeampi kuin matemaattisen Poisson-mallin antama arvio.
          </p>
        </div>

        <div className="grid gap-6">
          <InfoCard
            icon={Sigma}
            title="Mikä Poisson-malli on?"
            content="Poisson-malli arvioi joukkueiden odotetut maalimäärät ensisijaisesti kansainvälisistä 1X2-markkinakertoimista ja laskee niistä todennäköisyyden jokaiselle 0–0…5–5 lopputulokselle. Se on puhdas tilastollinen vertailupohja — ei tunne joukkueiden vireitä eikä loukkaantumisia. Jos yli/alle 2,5 -kertoimet ovat saatavilla, niitä käytetään 1X2-kertoimien rinnalla maalimäärän arvioinnissa, ja pienten tulosten (0–0, 1–0, 0–1, 1–1) todennäköisyyksiä tarkennetaan Dixon–Coles-korjauksella."
          />

          <InfoCard
            icon={Calculator}
            title="Mitä Poisson-kerroin tarkoittaa?"
            content="Poisson-kerroin on mallin laskema 'reilu' kerroin lopputulokselle. Esim. jos malli antaa kertoimen 8.50 ja Veikkauksen kerroin on 12.00, lopputulos on edullinen — Veikkaus maksaa siitä enemmän kuin mallin mukaan pitäisi."
          />

          <InfoCard
            icon={TrendingDown}
            title="Arvoveto tulosvedossa"
            color="underplayed"
            content="Kun Veikkauksen kerroin on selvästi korkeampi kuin Poisson-mallin kerroin, lopputuloksessa on potentiaalista arvoa. Suurin ero kannattaa tarkistaa erityisesti — mutta muista että malli ei näe joukkueen sen hetkistä tilannetta."
          />

          <InfoCard
            icon={TrendingUp}
            title="Yliarvostettu tulos"
            color="overplayed"
            content="Kun Veikkauksen kerroin on matalampi kuin Poisson-malli antaisi, lopputulos on suosittu eikä tarjoa hyvää arvoa. Tällaisissa kohteissa arvoa on yleensä vähemmän."
          />
        </div>

        <div className="pt-4 border-t border-border/40">
          <h2 className="text-2xl font-bold text-foreground mb-2">Näin käytät Monivetoa</h2>
          <p className="text-muted-foreground">
            Monivedossa (Veikkauksen MULTISCORE) arvataan 4–8 ottelun tarkat lopputulokset.
            Vetokone laskee jokaiselle ottelulle Poisson-jakauman ja vertaa sitä Veikkauksen
            kertoimiin, jotta arvokkaimmat yksittäiset tulokset ja rivit erottuvat.
          </p>
        </div>

        <div className="grid gap-6">
          <InfoCard
            icon={Trophy}
            title="Mikä Moniveto on?"
            content="Moniveto on Veikkauksen pooli-peli, jossa arvataan 4–8 ottelun tarkat lopputulokset (esim. 2–1, 0–0). Yhden rivin hinta on 0,10 €, ja jakopotti maksetaan täysin oikein arvanneiden kesken. Kohteet vaihtelevat kierroksittain — esim. Futismoniveto 8 sisältää kahdeksan ottelua."
          />

          <InfoCard
            icon={Sigma}
            title="Miten Vetokone auttaa Monivedossa?"
            content="Kullekin ottelulle näytetään sekä Veikkauksen tulos­taulukko että Poisson-mallin laskema jakauma. Näet suoraan, missä tuloksissa Veikkauksen kerroin on korkeampi kuin mallin reilu kerroin — niissä voi olla arvoa."
          />

          <InfoCard
            icon={Target}
            title="TOP-rivit ja odotusarvo"
            content="Vetokone yhdistelee jokaisen ottelun parhaat lopputulokset automaattisesti kokonaisiksi riviehdotuksiksi. Jokaiselle rivilistalle lasketaan osumistodennäköisyys ja odotusarvo, jolloin voit valita tavoitteesi mukaan (halvin täysosuma-yritys vai laajempi hajautus)."
          />

          <InfoCard
            icon={TrendingDown}
            title="Arvotulos Monivedossa"
            color="underplayed"
            content="Kun Veikkauksen kerroin yksittäiselle tulokselle on selvästi korkeampi kuin Poisson-mallin arvio, tulos on 'alihinnoiteltu' — malli antaa sille suuremman todennäköisyyden kuin Veikkauksen kerroin antaa ymmärtää. Näihin voi harkita hajauttamista."
          />
        </div>

        <div className="rounded-xl border border-border bg-card p-5 space-y-3">
          <h2 className="text-lg font-semibold text-foreground">Haluatko oppia lisää vedonlyönnistä?</h2>
          <p className="text-sm text-muted-foreground">
            Pitkäveto on Veikkauksen suosituin urheiluvedonlyöntipeli. Oppaassamme käydään läpi,
            miten löydät ylikertoimia vertaamalla Veikkauksen kertoimia markkinakertoimiin.
          </p>
          <Link
            to="/blogi/pitkaveto-ylikertoimet"
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Lue Pitkäveto-opas <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <section className="glass rounded-xl p-5 space-y-4" aria-labelledby="ukk">
          <h2 id="ukk" className="text-base font-semibold text-foreground">Usein kysytyt kysymykset</h2>
          <div className="space-y-4">
            {FAQ_ITEMS.map((item) => (
              <div key={item.q} className="space-y-1">
                <h3 className="text-sm font-semibold text-foreground">{item.q}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="glass rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-semibold text-foreground">Datalähteet</h3>
          <p className="text-sm text-muted-foreground">
            Peliprosentit ja kohteiden tiedot haetaan reaaliajassa suoraan Veikkauksen avoimista
            rajapinnoista (sport-open-games, sport-popularity ja sport-odds). Tulosvedon ja Monivedon
            Poisson-mallit lasketaan ensisijaisesti kansainvälisistä 1X2-markkinakertoimista; jos niitä ei ole saatavilla, pohjana käytetään Veikkauksen omia kertoimia. Mallin tuloksia verrataan Veikkauksen tuloskertoimiin.
          </p>
          <p className="text-sm text-muted-foreground">
            Vakion markkinakertoimet kerätään ensisijaisesti ESPN:n julkisesta scoreboard-rajapinnasta
            (DraftKings-moneyline isoille liigoille kuten Valioliiga, La Liga ja Serie A). Jos ESPN ei
            kata ottelua, haetaan varakertoimet The Odds API- ja Odds-API.io-palveluista. Kertoimet muunnetaan
            implisiittisiksi todennäköisyyksiksi (marginaali poistettuna) ja niitä verrataan Veikkauksen
            peliprosentteihin ali- ja ylipelattujen kohteiden tunnistamiseksi.
          </p>
        </div>

        <section id="tietoa" className="pt-4 border-t border-border/40 space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Tietoa Vetokoneesta</h2>
            <p className="text-muted-foreground">
              Vetokone on suomenkielinen työkalu, joka vertaa Veikkauksen Vakion, Tulosvedon ja
              Monivedon kertoimia kansainvälisiin markkinakertoimiin ja matemaattisiin malleihin.
              Tavoitteena on tehdä arvokohteiden tunnistaminen helpoksi ja läpinäkyväksi.
            </p>
          </div>

          <div className="grid gap-6">
            <InfoCard
              icon={History}
              title="Mistä kaikki alkoi"
              content="Kaikki lähti siitä, että Vakion peliprosentteja ja kansainvälisiä kertoimia vertailiin käsin. Huomattiin, että suomalaiset pelaajat usein yliarvostavat suosikkeja ja aliarvostavat tasaisia otteluita — ja että tästä syntyy toistuvasti arvokohteita. Aluksi vertailu tehtiin taulukkolaskennalla, sitten pienellä skriptillä, ja lopulta syntyi ajatus palvelusta, jossa kuka tahansa voi nähdä saman tiedon reaaliajassa."
            />

            <InfoCard
              icon={Scale}
              title="Miksi vertailu toimii"
              content="Veikkauksen peliprosentit eivät ole todennäköisyyksiä. Ne kertovat, miten suomalaiset pelaajat jakavat panoksensa eri merkkien välille. Kansainväliset markkinakertoimet puolestaan heijastavat laajempaa markkinaa ja ovat usein tarkempi arvio todellisista todennäköisyyksistä. Kun nämä kaksi kohtaavat, erot paljastavat selvästi ali- ja ylipelatut kohteet."
            />

            <InfoCard
              icon={Rocket}
              title="Miten palvelu kasvoi"
              content="Alkuvaiheessa mukana oli vain Vakion peliprosenttien vertailu. Pian mukaan tulivat Tulosvedon Poisson-malli ja Monivedon eli MULTISCOREn tulosjakaumat. Sitten lisättiin automaattiset rivisuositukset ja analyysit, jotka päivittyvät useita kertoja päivässä avoimien kierrosten ajan. Kehitys on ollut käyttäjien palautteen ja omien havaintojen ohjaamaa."
            />

            <InfoCard
              icon={Heart}
              title="Periaatteet"
              content="Vetokone on ilmainen käyttää. Se ei tarjoa vedonlyöntiä eikä ota välityspalkkioita. Datalähteet ja laskentamenetelmät kerrotaan avoimesti. Tavoite ei ole luvata voittoja, vaan antaa pelaajille paremmat työkalut omaan päätöksentekoon — ja pitää hauskuus ja vastuullisuus etusijalla."
            />
          </div>
        </section>

        <div className="glass rounded-xl p-5 space-y-3">
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-primary" />
            Haluatko kysyä tai antaa palautetta?
          </h2>
          <p className="text-sm text-muted-foreground">
            Jos sinulla on kysymyksiä, ideoita tai huomioita, kuulisimme mielellämme. Voit lähettää
            palautetta helposti omalla sivullaan.
          </p>
          <Link
            to="/palaute"
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <MessageSquare className="h-4 w-4" />
            Anna palautetta
          </Link>
        </div>
      </main>
    </div>
  );
}

function InfoCard({
  icon: Icon,
  title,
  content,
  color,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  content: string;
  color?: "underplayed" | "overplayed";
}) {
  return (
    <div className="glass rounded-xl p-5 space-y-2">
      <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
        <Icon
          className={`h-4 w-4 ${
            color === "underplayed"
              ? "text-underplayed"
              : color === "overplayed"
                ? "text-overplayed"
                : "text-primary"
          }`}
        />
        {title}
      </h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{content}</p>
    </div>
  );
}
